import { useEffect, useMemo, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { SectionTitle } from "./PilotBits";
import { errorMessage } from "../lib/api";
import { getInitials, avatarColorClass } from "../lib/avatar";
import { MAX_TEAM_SIZE, addableUsers, canManageMembers, intervenantsOf, removalPlan, roleInPilotLabel, teamOf } from "../lib/pilots";
import { Loader2, UserPlus, UserRound, Users, X } from "lucide-react";

const NONE = "__none__";

export function MemberAvatar({ member, size = 28 }) {
  const name = member.name || "Compte supprimé";
  return (
    <span
      role="img" aria-label={name} style={{ width: size, height: size }}
      className={`rounded-full inline-flex items-center justify-center text-2xs font-semibold overflow-hidden shrink-0 ${member.avatar_url ? "bg-slate-100" : avatarColorClass(name)}`}
    >
      {member.avatar_url ? <img src={member.avatar_url} alt="" className="w-full h-full object-cover" /> : getInitials(name)}
    </span>
  );
}

// A compact overlapping row of avatars (lists): at most `max` faces and "+N".
export function TeamStack({ members, total, max = 3 }) {
  const shown = (members || []).slice(0, max);
  const extra = Math.max(0, (total || shown.length) - shown.length);
  return (
    <span className="inline-flex items-center" data-testid="team-stack">
      {shown.map((m, i) => <span key={m.id} className={`ring-2 ring-white rounded-full ${i ? "-ml-2" : ""}`} title={m.name || undefined}><MemberAvatar member={m} size={24} /></span>)}
      {extra > 0 && <span className="ml-1.5 text-2xs font-semibold text-slate-500">+{extra}</span>}
    </span>
  );
}

// An intervenant without CRM account: neutral icon instead of an avatar (there is no account picture), "sans compte" badge.
function IntervenantChip({ person }) {
  return (
    <li data-testid={`team-intervenant-${person.id}`} title={person.job_title || undefined}
      className={`flex items-center gap-2 rounded-full border border-dashed pl-1 pr-3 py-1 text-sm ${person.inactive ? "border-slate-200 bg-slate-50 text-slate-400" : "border-slate-300 bg-white text-slate-800"}`}>
      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 inline-flex items-center justify-center shrink-0" aria-hidden="true"><UserRound size={13} /></span>
      <span className="truncate max-w-[10rem]">{person.name || "Intervenant"}</span>
      <span className="text-2xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">Sans compte</span>
    </li>
  );
}

// The team at a glance: faces, names, who is the principal and, for managers only, the way to change it.
export default function PilotTeamCard({ pilot, onManage }) {
  const team = teamOf(pilot);
  const intervenants = intervenantsOf(pilot);
  const size = team.length + intervenants.length;
  const manage = canManageMembers(pilot);
  const mine = roleInPilotLabel(pilot.role_in_pilot);
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-team-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <SectionTitle hint={`${size} personne${size > 1 ? "s" : ""}`}>Équipe</SectionTitle>
        </div>
        {manage && <Button variant="outline" size="sm" className="shrink-0 -mt-1" onClick={onManage} data-testid="pilot-team-manage"><Users size={14} className="mr-1.5" />Gérer</Button>}
      </div>
      <ul className="flex flex-wrap gap-2" data-testid="pilot-team-list">
        {team.map((m) => (
          <li key={m.id} data-testid={`team-member-${m.id}`} data-principal={m.is_principal} className={`flex items-center gap-2 rounded-full border pl-1 pr-3 py-1 text-sm ${m.is_active === false ? "border-slate-200 bg-slate-50 text-slate-400" : "border-slate-200 bg-white text-slate-800"}`}>
            <MemberAvatar member={m} size={24} />
            <span className="truncate max-w-[10rem]">{m.name || "Compte supprimé"}</span>
            {m.is_principal && <span className="text-2xs font-semibold uppercase tracking-wide text-teal-700 bg-teal-50 rounded px-1.5 py-0.5" data-testid="team-principal-badge">Principal</span>}
            {m.is_active === false && <span className="text-2xs font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">Inactif</span>}
          </li>
        ))}
        {intervenants.map((i) => <IntervenantChip key={i.id} person={i} />)}
      </ul>
      {intervenants.length > 0 && <p className="mt-2 text-xs text-slate-500">Les intervenants sans compte figurent dans l'équipe et peuvent être crédités du travail fait, mais n'ont pas accès au CRM.</p>}
      {mine && <p className="mt-3 text-xs text-slate-500" data-testid="pilot-my-role">Votre rôle sur ce pilote : {mine.toLowerCase()}.</p>}
    </Card>
  );
}

// Search the directory for collaborator fiches WITHOUT account (the backend applies the caller's directory scope).
function IntervenantSearch({ onSearch, onAdd, busy, disabled }) {
  const [query, setQuery] = useState("");
  const [state, setState] = useState({ status: "idle", items: [], error: "" });
  useEffect(() => {
    if (disabled) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setState((s) => ({ ...s, status: "loading" }));
      try {
        const items = await onSearch(query.trim());
        if (!cancelled) setState({ status: "ready", items, error: "" });
      } catch (e) {
        if (!cancelled) setState({ status: "error", items: [], error: errorMessage(e, "Recherche impossible pour le moment.") });
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, onSearch, disabled]);
  if (state.status === "error") return <p className="text-xs text-slate-500" data-testid="intervenant-search-error">{state.error}</p>;
  return (
    <div className="space-y-2" data-testid="intervenant-search">
      <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher dans l'annuaire (nom, fonction, référence)" aria-label="Rechercher un intervenant sans compte"
        className="text-base md:text-sm" disabled={disabled} data-testid="intervenant-search-input" />
      {state.status === "loading" && <div className="flex justify-center py-2"><Loader2 size={14} className="animate-spin text-slate-400" /></div>}
      {state.status === "ready" && state.items.length === 0 && <p className="text-xs text-slate-500">Aucune fiche sans compte ne correspond.</p>}
      {state.status === "ready" && state.items.length > 0 && (
        <ul className="max-h-48 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-md">
          {state.items.map((i) => (
            <li key={i.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900 truncate">{i.name}</div>
                <div className="text-xs text-slate-500 truncate">{[i.job_title, i.reference].filter(Boolean).join(" · ")}</div>
              </div>
              <Button size="sm" variant="outline" disabled={busy || disabled} onClick={async () => { if (await onAdd(i.id)) setState((st) => ({ ...st, items: st.items.filter((x) => x.id !== i.id) })); }} data-testid={`intervenant-add-${i.id}`}><UserPlus size={14} className="mr-1" />Ajouter</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Add, remove and re-appoint. A removal never moves tasks by itself: when the member holds open tasks the manager is offered a hand-over to a
// colleague (or leaves them with the person, who keeps them as history).
export function MembersDialog({ open, onClose, pilot, directory, teamTasks, busy, onAdd, onRemove, onChangePrincipal, onSearchIntervenants, onAddIntervenant, onRemoveIntervenant }) {
  const intervenants = intervenantsOf(pilot);
  const [toAdd, setToAdd] = useState("");
  const [pending, setPending] = useState(null);  // {type: "remove"|"principal", member}
  const [handOver, setHandOver] = useState(NONE);
  const [keepPrevious, setKeepPrevious] = useState(true);
  const team = teamOf(pilot);
  const { users, room } = useMemo(() => addableUsers({ pilot, directory }), [pilot, directory]);
  const plan = pending && pending.type === "remove" ? removalPlan({ pilot, memberId: pending.member.id, teamTasks }) : null;
  const close = () => { setPending(null); setToAdd(""); setHandOver(NONE); setKeepPrevious(true); onClose(); };
  const start = (type, member) => { setPending({ type, member }); setHandOver(NONE); setKeepPrevious(true); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="members-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Équipe du pilote</DialogTitle>
          <DialogDescription>Le principal coordonne ; les intervenants se répartissent le travail. {MAX_TEAM_SIZE} personnes au plus, principal compris.</DialogDescription>
        </DialogHeader>

        {!pending && (
          <>
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md" data-testid="members-list">
              {team.map((m) => (
                <li key={m.id} className="flex items-center gap-3 px-3 py-2" data-testid={`members-row-${m.id}`}>
                  <MemberAvatar member={m} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900 truncate">{m.name || "Compte supprimé"}{m.is_active === false && <span className="ml-2 text-2xs font-semibold uppercase text-amber-700">inactif</span>}</div>
                    <div className="text-xs text-slate-500">{m.is_principal ? "Principal" : "Intervenant"}</div>
                  </div>
                  {!m.is_principal && (
                    <div className="flex items-center gap-1 shrink-0">
                      {m.is_active !== false && <Button variant="ghost" size="sm" onClick={() => start("principal", m)} data-testid={`members-promote-${m.id}`}>Principal</Button>}
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" aria-label={`Retirer ${m.name || "ce membre"}`} onClick={() => start("remove", m)} data-testid={`members-remove-${m.id}`}><X size={14} /></Button>
                    </div>
                  )}
                </li>
              ))}
              {intervenants.map((i) => (
                <li key={i.id} className="flex items-center gap-3 px-3 py-2" data-testid={`members-intervenant-${i.id}`}>
                  <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 inline-flex items-center justify-center shrink-0" aria-hidden="true"><UserRound size={14} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900 truncate">{i.name || "Intervenant"}</div>
                    <div className="text-xs text-slate-500 truncate">Sans compte CRM{i.job_title ? ` · ${i.job_title}` : ""}</div>
                    {i.has_account && <div className="text-xs text-amber-700">A désormais un compte : ajoutez-le comme intervenant pour lui donner accès.</div>}
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 shrink-0" aria-label={`Retirer ${i.name || "cet intervenant"}`}
                    onClick={() => setPending({ type: "remove-intervenant", member: i })} data-testid={`members-remove-intervenant-${i.id}`}><X size={14} /></Button>
                </li>
              ))}
            </ul>

            <div className="space-y-2" data-testid="members-add">
              <Label htmlFor="members-add-select">Ajouter un intervenant</Label>
              {directory.length === 0 ? (
                <p className="text-xs text-slate-500">La liste des utilisateurs n'est pas disponible avec vos droits.</p>
              ) : room === 0 ? (
                <p className="text-xs text-amber-700" data-testid="members-full">L'équipe est complète ({MAX_TEAM_SIZE} personnes).</p>
              ) : (
                <div className="flex gap-2">
                  <Select value={toAdd} onValueChange={setToAdd}>
                    <SelectTrigger id="members-add-select" data-testid="members-add-select"><SelectValue placeholder="Choisir une personne" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button  disabled={!toAdd || busy} onClick={async () => { if (await onAdd([toAdd])) setToAdd(""); }} data-testid="members-add-confirm">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}<span className="ml-1.5 hidden sm:inline">Ajouter</span>
                  </Button>
                </div>
              )}
              {room > 0 && room <= 3 && <p className="text-xs text-slate-500">Il reste {room} place{room > 1 ? "s" : ""}.</p>}
            </div>

            {onSearchIntervenants && (
              <div className="space-y-2 border-t border-slate-100 pt-3" data-testid="members-add-intervenant">
                <Label>Ajouter un intervenant sans compte</Label>
                <p className="text-xs text-slate-500">Un technicien ou formateur externe de l'annuaire : il figure dans l'équipe et peut être crédité du travail, sans accès au CRM.</p>
                {room === 0 ? <p className="text-xs text-amber-700">L'équipe est complète ({MAX_TEAM_SIZE} personnes).</p>
                  : <IntervenantSearch onSearch={onSearchIntervenants} onAdd={onAddIntervenant} busy={busy} disabled={!open} />}
              </div>
            )}
          </>
        )}

        {pending && pending.type === "remove" && (
          <div className="space-y-3" data-testid="members-remove-panel">
            <p className="text-sm text-slate-700">Retirer <strong>{pending.member.name || "ce membre"}</strong> de l'équipe ? Son historique (comptes rendus, tâches terminées, journal) est conservé ; il perd l'accès au pilote.</p>
            {plan.openTasks > 0 ? (
              <div className="space-y-1.5 rounded-md bg-amber-50 border border-amber-200 p-3" data-testid="members-handover">
                <Label htmlFor="members-handover-select" className="text-amber-900">{plan.openTasks} tâche{plan.openTasks > 1 ? "s" : ""} ouverte{plan.openTasks > 1 ? "s" : ""} lui {plan.openTasks > 1 ? "sont" : "est"} assignée{plan.openTasks > 1 ? "s" : ""}</Label>
                <Select value={handOver} onValueChange={setHandOver}>
                  <SelectTrigger id="members-handover-select" data-testid="members-handover-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Les lui laisser</SelectItem>
                    {plan.candidates.map((m) => <SelectItem key={m.id} value={m.id}>Réattribuer à {m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : <p className="text-xs text-slate-500">Aucune tâche ouverte à réattribuer.</p>}
          </div>
        )}

        {pending && pending.type === "remove-intervenant" && (
          <div className="space-y-3" data-testid="members-remove-intervenant-panel">
            <p className="text-sm text-slate-700">Retirer <strong>{pending.member.name || "cet intervenant"}</strong> de l'équipe ? Il reste crédité sur les comptes rendus et les tâches déjà réalisés.</p>
          </div>
        )}

        {pending && pending.type === "principal" && (
          <div className="space-y-3" data-testid="members-principal-panel">
            <p className="text-sm text-slate-700">Désigner <strong>{pending.member.name}</strong> comme principal du pilote ?</p>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Checkbox checked={keepPrevious} onCheckedChange={(v) => setKeepPrevious(v === true)} data-testid="members-keep-previous" />
              Garder l'ancien principal comme intervenant
            </label>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {pending ? (
            <>
              <Button variant="outline" onClick={() => setPending(null)} disabled={busy}>Retour</Button>
              <Button
                className={pending.type === "principal" ? undefined : "bg-red-600 hover:bg-red-700 text-white"} disabled={busy} data-testid="members-pending-confirm"
                onClick={async () => {
                  const ok = pending.type === "remove" ? await onRemove(pending.member.id, handOver === NONE ? null : handOver)
                    : pending.type === "remove-intervenant" ? await onRemoveIntervenant(pending.member.id)
                      : await onChangePrincipal(pending.member.id, keepPrevious);
                  if (ok) setPending(null);
                }}
              >
                {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{pending.type === "principal" ? "Désigner comme principal" : "Retirer de l'équipe"}
              </Button>
            </>
          ) : <Button variant="outline" onClick={close} data-testid="members-close">Fermer</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
